// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * AddressLookupService - V40
 *
 * Gebruikt de gratis Nederlandse BAG Locatieserver (PDOK) voor adres-autofill.
 * API: https://api.pdok.nl/bzk/locatieserver/search/v3_1/free
 * Geen API-key nodig. Geen verwerkersovereenkomst vereist (open overheidsdata).
 *
 * Privacy: alleen postcode + huisnummer worden verstuurd naar de PDOK-server
 * om straatnaam en woonplaats op te halen. Er worden geen persoonsgegevens verstuurd.
 * De PDOK-server is een dienst van het Kadaster (Rijksoverheid).
 */

export interface AddressResult {
  street:  string
  city:    string
  found:   boolean
}

export type LookupStatus = 'idle' | 'loading' | 'found' | 'not_found'

// ── Normalisatie ──────────────────────────────────────────────────────────────

function normalizePostalCode(pc: string): string {
  return pc.replace(/\s/g, '').toUpperCase()
}

function normalizeHouseNumber(nr: string): string {
  return nr.trim().replace(/^0+/, '') || nr.trim()
}

// ── BAG Locatieserver (PDOK) ──────────────────────────────────────────────────

async function bagLookup(postalCode: string, houseNumber: string): Promise<AddressResult> {
  const query = encodeURIComponent(`${postalCode} ${houseNumber}`)
  const url   = `https://api.pdok.nl/bzk/locatieserver/search/v3_1/free?q=${query}&rows=1&fq=type:adres`

  // AbortSignal.timeout() is Safari 16+ only - veilige fallback voor oudere iPhones
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 5000)
  let response: Response
  try {
    response = await fetch(url, { headers: { Accept: 'application/json' }, signal: controller.signal })
  } finally {
    clearTimeout(timer)
  }

  if (!response.ok) return { street: '', city: '', found: false }

  const data = await response.json()
  const doc  = data?.response?.docs?.[0]

  if (!doc?.straatnaam || !doc?.woonplaatsnaam) {
    return { street: '', city: '', found: false }
  }

  return {
    street: doc.straatnaam,
    city:   doc.woonplaatsnaam,
    found:  true,
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Zoek straatnaam en woonplaats op basis van postcode en huisnummer.
 * Geeft altijd een resultaat - nooit een exception naar de aanroeper.
 * Bij een fout of geen resultaat: found=false, handmatige invoer blijft mogelijk.
 */
export async function lookupAddress(
  postalCode: string,
  houseNumber: string
): Promise<AddressResult> {
  const pc = normalizePostalCode(postalCode)
  const nr = normalizeHouseNumber(houseNumber)

  // Minimale validatie: Nederlandse postcode = 4 cijfers + 2 letters
  if (!/^\d{4}[A-Z]{2}$/.test(pc) || !nr) {
    return { street: '', city: '', found: false }
  }

  try {
    return await bagLookup(pc, nr)
  } catch {
    // Netwerk fout, timeout of parse fout - handmatige invoer als fallback
    return { street: '', city: '', found: false }
  }
}
